import React from 'react';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { DirectionsBoat, Flight, LocalPolice, LocalShipping, Train } from '@vedmarket/ui-kit';

import { APPLICATION_SHIPPING_CODE, COLORS } from 'config/constants';
import { getPluralForm } from 'utils/validation';
import { getFlag } from '../../helpers';

import './result-route.scss';

const getTransportIcon = (freight?: Freight) => {
  const iconProps = { className: 'delivery-result-route__transport-icon' };

  switch (String(freight?.shipping_method.code)) {
    case APPLICATION_SHIPPING_CODE.SEA:
      return <DirectionsBoat {...iconProps} />;
    case APPLICATION_SHIPPING_CODE.AUTO:
      return <LocalShipping {...iconProps} />;
    case APPLICATION_SHIPPING_CODE.AVIA:
      return <Flight {...iconProps} />;
    case APPLICATION_SHIPPING_CODE.RAILS:
      return <Train {...iconProps} />;
    default:
      return null;
  }
};

const dateTypographySx = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: 0,
  color: COLORS.BLACK_DARK,
};

const captionTypographySx = {
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.3,
  minHeight: '16px',
  letterSpacing: 0,
};

const ResultRoute = ({ route, hintsOpen }: DeliveryResultRouteProps) => {
  const { t } = useTranslation();
  const dayForms: PluralForms = [
    t('declension__text__daysOne'),
    t('declension__text__daysSeveral'),
    t('declension__text__daysMany'),
  ];
  const hasDuration = route.min_days !== undefined && route.max_days !== undefined;
  const duration = hasDuration
    ? `${route.min_days}–${route.max_days} ${getPluralForm(route.max_days || 0, dayForms)}`
    : '';
  const locations = [ route.location_from, ...route.freights.map(freight => freight.location_to) ];
  const points: DeliveryResultPoint[] = locations.map((location, index) => ({
    location,
    freight: route.freights[index],
    isCustoms: index > 0 && route.freights[index - 1]?.customs_point?.location?.code === location.code,
  }));
  const isWrapped = points.length === 4;
  const rows = isWrapped
    ? [ [ points[0], points[1], undefined ], [ undefined, points[2], points[3] ] ]
    : [ points ];

  const getTransportTitle = (freight?: Freight) => {
    if (freight?.min_days === undefined || freight.max_days === undefined) {
      return freight?.shipping_method.name || '';
    }

    const transitDuration = freight.min_days === freight.max_days
      ? String(freight.max_days)
      : `${freight.min_days}–${freight.max_days}`;

    return t('calc__text__delivery__approximateDays', {
      count: freight.max_days,
      duration: transitDuration,
    });
  };

  return <div className={`delivery-result-route${isWrapped ? ' delivery-result-route_wrapped' : ''}`}>
    {rows.map((row, rowIndex) => <div className='delivery-result-route__row' key={rowIndex}>
      <div className={`delivery-result-route__dates${rowIndex ? ' delivery-result-route__dates_arrival' : ''}`}>
        {!rowIndex && <>
          <Typography variant='body2' sx={dateTypographySx}>
            {moment(route.planned_shipment_date).format('DD.MM.YYYY')}
          </Typography>
          <Typography variant='body2' sx={dateTypographySx}>{duration}</Typography>
        </>}
        <Typography variant='body2' className={isWrapped && !rowIndex ? 'delivery-result-route__arrival-hidden' : undefined} sx={dateTypographySx}>
          {moment(route.planned_delivery_date).format('DD.MM.YYYY')}
        </Typography>
      </div>
      <div className='delivery-result-route__points'>
        {row.map((point, pointIndex) => {
          const isLastColumn = pointIndex === row.length - 1;
          const columnClass = isLastColumn ? ' delivery-result-route__point_last' : '';

          if (!point) {
            const continuationClass = rowIndex ? ' delivery-result-route__point_leading' : ' delivery-result-route__point_trailing';

            return <div className={`delivery-result-route__point${columnClass}${continuationClass}`} key={`continuation-${rowIndex}`}>
              <div className='delivery-result-route__plan'>
                <span className='delivery-result-route__line' />
                {rowIndex > 0 && <Tooltip title={getTransportTitle(points[1].freight)}>
                  <span className='delivery-result-route__transport'>{getTransportIcon(points[1].freight)}</span>
                </Tooltip>}
              </div>
            </div>;
          }

          const routeIndex = points.indexOf(point);
          const isFirst = routeIndex === 0;
          const isLast = routeIndex === points.length - 1;
          const sign = isFirst ? 'A' : 'B';
          const hasContinuation = pointIndex < row.length - 1 && !row[pointIndex + 1];
          const lineClass = hasContinuation ? ' delivery-result-route__line_continued' : '';
          const showTransitHint = hintsOpen && isFirst;
          const showCustomsHint = hintsOpen && point.isCustoms;

          return <div className={`delivery-result-route__point${columnClass}`} key={`${point.location.id}-${routeIndex}`}>
            <Tooltip title={point.location.name}>
              <div className='delivery-result-route__city'>
                {getFlag({ code: point.location.country.code, width: '20px', marginRight: '0' })}
                <Typography variant='caption' sx={{ ...captionTypographySx, color: COLORS.BLACK_DARK }}>
                  {point.location.name}
                </Typography>
              </div>
            </Tooltip>
            <Typography variant='caption' className='delivery-result-route__code' sx={{ ...captionTypographySx, color: COLORS.LIGHT_TEXT_DISABLED }}>
              {point.location.code}
            </Typography>
            <div className='delivery-result-route__plan'>
              <Typography component='span' className='delivery-result-route__marker' sx={{
                fontSize: '12px',
                lineHeight: '20px',
                fontWeight: 900,
                letterSpacing: '0.4px',
                color: COLORS.BLUE,
              }}>
                {isFirst || isLast ? sign : <span className='delivery-result-route__dot' />}
              </Typography>
              {!isLast && <>
                <span className={`delivery-result-route__line${lineClass}`} />
                <Tooltip title={getTransportTitle(point.freight)} placement='bottom' arrow open={hintsOpen === undefined ? undefined : Boolean(showTransitHint)}
                  slotProps={{
                    popper: {
                      modifiers: [ { name: 'offset', options: { offset: [ 0, -2 ] } } ],
                    },
                  }}>
                  <span className='delivery-result-route__transport'>{getTransportIcon(point.freight)}</span>
                </Tooltip>
              </>}
              {point.isCustoms && <Tooltip title={t('common__text__customs')} placement='bottom' arrow open={hintsOpen === undefined ? undefined : Boolean(showCustomsHint)}
                slotProps={{
                  popper: {
                    modifiers: [ { name: 'offset', options: { offset: [ 0, -8 ] } } ],
                  },
                }}>
                <span className='delivery-result-route__customs'><LocalPolice className='delivery-result-route__customs-icon' /></span>
              </Tooltip>}
            </div>
          </div>;
        })}
      </div>
    </div>)}
  </div>;
};

export default ResultRoute;
